import { useEffect, useState } from "react";
import type { PaymentMethodPayload } from "@/interfaces/payment-method";
import { toast } from "sonner";
import type { TransactionInterface } from "@/interfaces/transaction";
import type { Trade, TradeInterface } from "@/interfaces/trade";
import type {
  Notification,
  NotificationInterface,
} from "@/interfaces/notification";
import { useQuery } from "@tanstack/react-query";
import { makeRequest } from "@/utils/makeRequest";
import type { UserProfile, UserReputationScore } from "@/interfaces/profile";
import type { AnalyticsResponse } from "@/interfaces/analytics";
import type { Currency } from "@/interfaces/currency";
import type {
  Advertisement,
  AdvertisementInterface,
} from "@/interfaces/advertisement";
import type { Country } from "@/interfaces/country";
import type { KycSubmission, KycSubmissionInterface } from "@/interfaces/kyc";
import type { SupportTicketInterface } from "@/interfaces/ticket";
import type { PaymentMethod } from "@/interfaces/payment-method";
import type { Dispute, DisputeInterface } from "@/interfaces/dispute";
import type { Wallet, WalletAnalytics } from "@/interfaces/wallet";
import { useMutation } from "@tanstack/react-query";
import { queryClient } from "@/services/queryClient";
import type { Token, TokenInterface } from "@/interfaces/token";
import type { MerchantReviewInterface } from "@/interfaces/review";
import type { Session } from "@/interfaces/session";
import type { AdminSettings } from "@/interfaces/admin-setting";
import type {
  CreateTransactionSettingPayload,
  TransactionTierSetting,
  TransactionTierSettingFilters,
  UpdateTransactionSettingPayload,
} from "@/interfaces/transaction-tier-settings";

type UseTransactionsParams = {
  page?: number;
  limit?: number;
};

type UseParams = {
  page?: number;
  limit?: number;
};

export const useProfile = (userId: string) => {
  return useQuery<UserProfile>({
    queryKey: ["profile", userId],
    enabled: !!userId,
    queryFn: async () => {
      const res = await makeRequest.get("/user/me");
      console.log(res.data);
      return res.data;
    },
  });
};

export const useTradingPerformance = (userId: string) => {
  return useQuery({
    queryKey: ["trade-performance", userId],
    enabled: !!userId,
    queryFn: async () => {
      const res = await makeRequest.get("/trade/performance");
      return res.data;
    },
  });
};

export const useSingleUser = (id: string) => {
  return useQuery<UserProfile>({
    queryKey: ["user", id],
    enabled: !!id,
    queryFn: async () => {
      const res = await makeRequest.get(`/user/${id}`);
      return res.data;
    },
  });
};

export const useSystemSettings = () => {
  return useQuery<AdminSettings>({
    queryKey: ["admin-settings"],

    queryFn: async () => {
      const res = await makeRequest.get("/admin-setting");

      return res.data;
    },
  });
};

export function useUpdateUser(id: string) {
  return useMutation({
    mutationFn: async (payload: any) => {
      const res = await makeRequest.put(`/user/${id}`, payload);
      return res.data;
    },
  });
}

export const searchUsers = async (query: string) => {
  if (!query || query.trim().length < 3) return [];

  const res = await makeRequest.get("/user/resolve", {
    params: { q: query },
  });

  return res.data;
};

export const useUserSearch = (query: string) => {
  return useQuery({
    queryKey: ["user-search", query],
    queryFn: () => searchUsers(query),
    enabled: !!query && query.trim().length >= 3,
    staleTime: 1000 * 60, // 1 min cache
  });
};

export const useMyWalletAndAssets = (userId: string) => {
  return useQuery<Wallet>({
    queryKey: ["wallet", userId],
    enabled: !!userId,
    queryFn: async () => {
      const res = await makeRequest.get("/wallet/me");

      console.log("WALLET RESPONSE FROM API", res.data);
      return res.data;
    },
  });
};

export const useSystemWalletDetails = (systemType: string) => {
  const normalizedSystemType = systemType?.trim().toUpperCase();

  return useQuery<Wallet>({
    queryKey: ["wallet:details", systemType],
    enabled: !!normalizedSystemType,
    staleTime: 1000 * 60,
    retry: 2,
    queryFn: async () => {
      const res = await makeRequest.get(
        `/wallet/system/${normalizedSystemType}`,
      );
      return res.data;
    },
  });
};

export const useAuditLogs = (params: {
  page: number;
  search: string;
  level: string;
  category: string;
  dateFilter: string;
}) => {
  return useQuery({
    queryKey: ["audit-logs", params], // Important: include all filters in key
    queryFn: async () => {
      const { data } = await makeRequest.get("/audit", {
        params: {
          page: params.page,
          limit: 10,
          search: params.search || undefined,
          level: params.level === "all" ? undefined : params.level,
          category: params.category === "all" ? undefined : params.category,
          dateFilter:
            params.dateFilter === "all" ? undefined : params.dateFilter,
        },
      });
      return data;
    },
    // keepPreviousData: true, // Smooth UX when changing pages/filters
  });
};

// API call
const fetchAnalytics = async (): Promise<AnalyticsResponse> => {
  const { data } = await makeRequest.get("/analytics/overview");
  return data;
};

export const useAnalytics = (timeRange: string = "30d") => {
  return useQuery({
    queryKey: ["analytics", timeRange],
    queryFn: fetchAnalytics,
    staleTime: 1000 * 60 * 5,
  });
};

export const useCurrencies = () => {
  return useQuery<Currency[]>({
    queryKey: ["currencies:all"],
    queryFn: async () => {
      const res = await makeRequest.get("/currency");

      return res.data;
    },
  });
};

import { useInfiniteQuery } from "@tanstack/react-query";
import type {
  AdvertisementFilters,
  AdvertisementPage,
} from "@/components/p2p/filter/interface";

export const useAdvertisments = (filters?: AdvertisementFilters) => {
  return useInfiniteQuery<AdvertisementPage>({
    queryKey: [
      "advertisments:all",
      filters?.side ?? "",
      filters?.crypto ?? "",
      filters?.fiat ?? "",
      filters?.minAmount ?? "",
      filters?.maxAmount ?? "",
      filters?.search ?? "",
      filters?.paymentMethod ?? "",
    ],

    queryFn: async ({ pageParam }) => {
      const params: Record<string, string | number> = {
        page: pageParam as number,
        limit: 10,
      };

      if (filters?.side) {
        params.side = filters.side;
      }

      if (filters?.crypto) {
        params.crypto = filters.crypto;
      }

      if (filters?.fiat) {
        params.fiat = filters.fiat;
      }

      if (filters?.minAmount?.trim()) {
        const numericMinAmount = Number(filters.minAmount);

        if (Number.isFinite(numericMinAmount) && numericMinAmount >= 0) {
          params.minAmount = numericMinAmount;
        }
      }

      if (filters?.maxAmount?.trim()) {
        const numericMaxAmount = Number(filters.maxAmount);

        if (Number.isFinite(numericMaxAmount) && numericMaxAmount >= 0) {
          params.maxAmount = numericMaxAmount;
        }
      }

      if (filters?.search?.trim()) {
        params.search = filters.search.trim();
      }

      if (filters?.paymentMethod) {
        params.paymentMethod = filters.paymentMethod;
      }

      const res = await makeRequest.get("/advertisement", {
        params,
      });

      return res.data;
    },

    initialPageParam: 1,

    getNextPageParam: (lastPage) => {
      const { page, totalPages } = lastPage.meta;

      return page < totalPages ? page + 1 : undefined;
    },

    staleTime: 30_000,
  });
};

// import { useInfiniteQuery } from "@tanstack/react-query";

// export const useAdvertisments = () => {
//   return useInfiniteQuery<AdvertisementInterface>({
//     queryKey: ["advertisments:all"],
//     queryFn: async ({ pageParam }) => {
//       const res = await makeRequest.get("/advertisement", {
//         params: {
//           page: pageParam,
//           limit: 10,
//         },
//       });

//       return res.data;
//     },
//     initialPageParam: 1,
//     getNextPageParam: (lastPage) => {
//       const { page, totalPages } = lastPage.meta;

//       return page < totalPages ? page + 1 : undefined;
//     },
//   });
// };

export const useMerchantAds = (merchantId: string) => {
  return useQuery<Advertisement[]>({
    queryKey: ["merchant:ad", merchantId],
    enabled: !!merchantId,
    queryFn: async () => {
      const res = await makeRequest.get(
        `/advertisement/merchant/${merchantId}`,
      );

      return res.data.data;
    },
  });
};

export const useTokens = (page = 1, limit = 20, search = "") => {
  return useQuery<TokenInterface>({
    queryKey: ["tokens", page, limit, search],
    queryFn: async () => {
      const { data } = await makeRequest.get("/token", {
        params: {
          page,
          limit,
          search,
        },
      });

      return data;
    },
  });
};

export const useNoPaginationTokens = () => {
  return useQuery<Token[]>({
    queryKey: ["tokens:no-pagination"],
    queryFn: async () => {
      const { data } = await makeRequest.get("/token/no-pagination");

      return data;
    },
  });
};

export const useCountries = () => {
  return useQuery<Country[]>({
    queryKey: ["countries:all"],
    queryFn: async () => {
      const res = await makeRequest.get("/country");

      return res.data;
    },
  });
};

export const useKycSubmissions = (params?: UseParams) => {
  const page = params?.page ?? 1;
  const limit = params?.limit ?? 10;

  return useQuery<KycSubmissionInterface>({
    queryKey: ["kyc-submissions:all", page, limit],
    queryFn: async () => {
      const res = await makeRequest.get("/kyc-submission", {
        params: { page, limit },
      });

      console.log("KYC FROM API", res.data);

      return res.data;
    },
  });
};

export const useKycSubmission = (id: string) => {
  return useQuery<KycSubmission>({
    queryKey: ["kyc-submission", id],
    enabled: !!id,
    queryFn: async () => {
      const res = await makeRequest.get(`/kyc-submission/${id}`);

      return res.data;
    },
  });
};

export const useMyNotifications = (userId: string, params?: UseParams) => {
  const page = params?.page ?? 1;
  const limit = params?.limit ?? 10;

  return useQuery<NotificationInterface>({
    queryKey: ["notifications:me", page, limit, userId],
    enabled: !!userId && !!page && !!limit,
    queryFn: async () => {
      const res = await makeRequest.get("/notification/me", {
        params: { page, limit },
      });

      return res.data;
    },
  });
};

export const useMyUnreadCount = (userId: string) => {
  return useQuery({
    queryKey: ["unread-count", userId],
    enabled: !!userId,
    queryFn: async () => {
      const res = await makeRequest.get("/notification/unread/count");

      return res.data;
    },
  });
};

export const useTransactions = (params?: UseTransactionsParams) => {
  const page = params?.page ?? 1;
  const limit = params?.limit ?? 10;

  return useQuery<TransactionInterface>({
    queryKey: ["transactions:all", page, limit],
    queryFn: async () => {
      const res = await makeRequest.get("/transaction", {
        params: { page, limit },
      });

      return res.data;
    },
    // keepPreviousData: true, // ✅ important for pagination UX
  });
};

export const useUserTransactions = (id: string) => {
  return useQuery<TransactionInterface>({
    queryKey: ["transactions:user", id],
    enabled: !!id,
    queryFn: async () => {
      const res = await makeRequest.get(`/transaction/user/${id}`);

      return res.data;
    },
  });
};

export const useTrade = (tradeId: string, userId: string) => {
  return useQuery<Trade>({
    queryKey: ["trade", tradeId, userId],
    enabled: !!tradeId,
    queryFn: async () => {
      const res = await makeRequest.get(`/trade/${tradeId}`);
      return res.data;
    },
  });
};

export const useMyTrades = (userId: string) => {
  return useQuery<Trade[]>({
    queryKey: ["trades", userId],
    enabled: !!userId,
    queryFn: async () => {
      const res = await makeRequest.get(`/trade/me`);
      return res.data;
    },
  });
};

export const useMyTickets = (userId: string) => {
  return useQuery<SupportTicketInterface>({
    queryKey: ["tickets", userId],
    enabled: !!userId,
    queryFn: async () => {
      const res = await makeRequest.get(`/ticket/me`);
      return res.data;
    },
  });
};

export const useTickets = () => {
  return useQuery<SupportTicketInterface>({
    queryKey: ["tickets"],
    queryFn: async () => {
      const res = await makeRequest.get(`/ticket`);
      return res.data;
    },
  });
};

export const useWalletAnalytics = () => {
  return useQuery<WalletAnalytics>({
    queryKey: ["wallet-analytics"],
    queryFn: async () => {
      const res = await makeRequest.get(`/wallet/analytics`);

      return res.data;
    },
  });
};

export const useTrades = () => {
  return useQuery<TradeInterface>({
    queryKey: ["all-trades"],
    queryFn: async () => {
      const res = await makeRequest.get(`/trade`);
      return res.data;
    },
  });
};

export const useSystemNotifications = (params?: UseParams) => {
  const page = params?.page ?? 1;
  const limit = params?.limit ?? 10;

  return useQuery<NotificationInterface>({
    queryKey: ["notifications-system", page, limit],
    enabled: !!page && !!limit,
    queryFn: async () => {
      const res = await makeRequest.get(`/notification/system`, {
        params: { page, limit },
      });
      return res.data;
    },
  });
};

export const useAssetDetails = (id: string, userId: string) => {
  return useQuery({
    queryKey: ["wallet-asset", id, userId],
    enabled: !!id && !!userId,
    queryFn: async () => {
      const res = await makeRequest.get(`/wallet/by-id/${id}`);
      return res.data;
    },
  });
};

export const useLedgerEntries = () => {
  return useQuery({
    queryKey: ["ledger-entries"],
    queryFn: async () => {
      const res = await makeRequest.get(`/ledger-entry/all`);
      return res.data;
    },
  });
};

export const useRates = () => {
  return useQuery({
    queryKey: ["rates:all"],
    queryFn: async () => {
      const res = await makeRequest.get(`/exchange-rate/db`);
      return res.data;
    },
  });
};

export const useAd = (id: string, userId: string) => {
  return useQuery<Advertisement>({
    queryKey: ["ad", id, userId],
    enabled: !!id,
    queryFn: async () => {
      const res = await makeRequest.get(`/advertisement/${id}/me`);
      return res.data;
    },
  });
};

export const useRate = (param: { from: string; to: string }) => {
  return useQuery({
    queryKey: ["rate", param.from, param.to],
    enabled: !!param,
    queryFn: async () => {
      const res = await makeRequest.get(
        `/exchange-rate/single?from=${param.from}&&to=${param.to}`,
      );
      return res.data;
    },
  });
};

export const usePaymentMethods = (userId: string) => {
  return useQuery({
    queryKey: ["payment-methods", userId],

    queryFn: async () => {
      const res = await makeRequest.get("/payment-methods");

      return res.data as PaymentMethod[];
    },

    enabled: !!userId,
  });
};

export const useUserReputationScore = (userId: string) => {
  return useQuery<UserReputationScore>({
    queryKey: ["user-score", userId],
    enabled: !!userId,

    queryFn: async () => {
      const res = await makeRequest.get(`/reputation/${userId}`);

      return res.data;
    },
  });
};

export const useDisputes = (params?: UseParams) => {
  const page = params?.page ?? 1;
  const limit = params?.limit ?? 10;

  return useQuery<DisputeInterface>({
    queryKey: ["disputes", page, limit],
    queryFn: async () => {
      const res = await makeRequest.get("/dispute", {
        params: { page, limit },
      });

      return res.data;
    },
  });
};

export const useDispute = (id: string) => {
  return useQuery<Dispute>({
    queryKey: ["disputes", id],
    queryFn: async () => {
      const res = await makeRequest.get(`/dispute/${id}`);

      return res.data;
    },
  });
};

// Resolve later
export const usePaymentMethodss = () => {
  const [data, setData] = useState<PaymentMethod[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchPaymentMethods = async () => {
    setLoading(true);
    try {
      const res = await makeRequest.get("/payment-methods");

      setData(res.data);
    } catch (err: any) {
      toast.error(err.message || "Failed to fetch payment methods");
    } finally {
      setLoading(false);
    }
  };

  const createPaymentMethod = async (payload: PaymentMethodPayload) => {
    try {
      const res = await makeRequest({
        method: "POST",
        url: "/payment-methods",
        data: payload,
      });

      setData((prev) => [res.data, ...prev]);
      toast.success("Payment method added");
    } catch (err: any) {
      toast.error(err.message || "Failed to add payment method");
    }
  };

  const updatePaymentMethod = async (
    id: string,
    payload: PaymentMethodPayload,
  ) => {
    try {
      const res = await makeRequest({
        method: "PATCH",
        url: `/payment-methods/${id}`,
        data: payload,
      });

      setData((prev) => prev.map((item) => (item.id === id ? res.data : item)));

      toast.success("Payment method updated");
    } catch (err: any) {
      toast.error(err.message || "Failed to update payment method");
    }
  };

  const deletePaymentMethod = async (id: string) => {
    try {
      await makeRequest({
        method: "DELETE",
        url: `/payment-methods/${id}`,
      });

      setData((prev) => prev.filter((item) => item.id !== id));
      toast.success("Payment method deleted");
    } catch (err: any) {
      toast.error(err.message || "Failed to delete payment method");
    }
  };

  const setDefault = async (id: string) => {
    try {
      const res = await makeRequest({
        method: "PATCH",
        url: `/payment-methods/${id}/set-default`,
      });

      setData(res.data);
      toast.success("Default payment method updated");
    } catch (err: any) {
      toast.error("Failed to set default");
    }
  };

  const toggleStatus = async (id: string) => {
    try {
      const res = await makeRequest({
        method: "PATCH",
        url: `/payment-methods/${id}/toggle`,
      });

      setData((prev) => prev.map((item) => (item.id === id ? res.data : item)));
    } catch (err: any) {
      toast.error("Failed to update status");
    }
  };

  useEffect(() => {
    fetchPaymentMethods();
  }, []);

  return {
    data,
    loading,
    fetchPaymentMethods,
    createPaymentMethod,
    updatePaymentMethod,
    deletePaymentMethod,
    setDefault,
    toggleStatus,
  };
};

export const addAsset = async (dto: { tokenId: string }) => {
  const { data } = await makeRequest.post("/wallet/assets/add", dto);

  return data;
};

export const toggleAsset = async (assetId: string) => {
  const { data } = await makeRequest.put(`/wallet/assets/${assetId}/toggle`);

  return data;
};

export const useAddAsset = (userId: string) => {
  return useMutation({
    mutationFn: addAsset,

    onSuccess: (asset) => {
      queryClient.setQueryData(["wallet", userId], (old: any) => {
        if (!old) return old;

        const exists = old.assets.some((a: any) => a.id === asset.id);

        if (exists) {
          return {
            ...old,
            assets: old.assets.map((a: any) => (a.id === asset.id ? asset : a)),
          };
        }

        return {
          ...old,
          assets: [...old.assets, asset],
        };
      });

      queryClient.invalidateQueries({
        queryKey: ["tokens"],
      });
    },

    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: ["wallet", userId],
      });

      queryClient.invalidateQueries({
        queryKey: ["tokens"],
        exact: false,
      });
    },
  });
};

export const useToggleAsset = (userId: string) => {
  return useMutation({
    mutationFn: toggleAsset,

    onMutate: async (assetId) => {
      await queryClient.cancelQueries({
        queryKey: ["wallet", userId],
      });

      const previousWallet = queryClient.getQueryData(["wallet", userId]);

      queryClient.setQueryData(["wallet", userId], (old: any) => {
        if (!old) return old;

        return {
          ...old,
          assets: old.assets.filter((a: any) => a.id !== assetId),
        };
      });

      return { previousWallet };
    },

    onError: (_, __, context) => {
      queryClient.setQueryData(["wallet", userId], context?.previousWallet);
    },

    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["wallet", userId],
      });
    },

    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: ["wallet", userId],
      });

      queryClient.invalidateQueries({
        queryKey: ["tokens"],
        exact: false,
      });
    },
  });
};

export const useCreateReview = async () => {};

export const useMerchantReviews = (targetUserId: string) => {
  return useQuery<MerchantReviewInterface>({
    queryKey: ["reviews-merchant", targetUserId],
    enabled: !!targetUserId,
    queryFn: async () => {
      const res = await makeRequest.get(`/review/${targetUserId}`);
      return res.data;
    },
  });
};

export const useMySessions = (userId: string) => {
  return useQuery<Session[]>({
    queryKey: ["my-sessions", userId],
    enabled: !!userId,
    queryFn: async () => {
      const res = await makeRequest.get("/auth/sessions");

      console.log("SESSIONS FROM API", res);

      return res.data.data;
    },
  });
};

export const useDepositAddress = (id: string) => {
  return useQuery({
    queryKey: ["wallet-address", id],
    enabled: !!id,
    queryFn: async () => {
      const res = await makeRequest.get(`/wallet/wallet-address/${id}`);

      return res.data.data;
    },
  });
};

// TRANSACTION TIER SETTINGS
// ============================================================
// TRANSACTION TIER SETTINGS
// ============================================================
export const useTransactionSettings = (
  filters?: TransactionTierSettingFilters,
) => {
  return useQuery<TransactionTierSetting[]>({
    queryKey: ["transaction-settings", filters],

    queryFn: async () => {
      const res = await makeRequest.get("/transaction-setting", {
        params: {
          ...(filters?.type ? { type: filters.type } : {}),
          ...(filters?.tier ? { tier: filters.tier } : {}),
          ...(filters?.enabled !== undefined
            ? { enabled: filters.enabled }
            : {}),
        },
      });

      return res.data;
    },
  });
};

export const useTransactionSetting = (id?: string) => {
  return useQuery<TransactionTierSetting>({
    queryKey: ["transaction-setting", id],

    enabled: !!id,

    queryFn: async () => {
      const res = await makeRequest.get(`/transaction-setting/${id}`);

      return res.data;
    },
  });
};

export const useCreateTransactionSetting = () => {
  return useMutation({
    mutationFn: async (payload: CreateTransactionSettingPayload) => {
      const res = await makeRequest.post("/transaction-setting", payload);

      return res.data;
    },

    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ["transaction-settings"],
      });

      toast.success("Transaction policy created successfully.");
    },

    onError: (error: any) => {
      toast.error(
        error?.response?.data?.message ||
          "Failed to create transaction policy.",
      );
    },
  });
};

export const useUpdateTransactionSetting = () => {
  return useMutation({
    mutationFn: async ({
      id,
      payload,
    }: {
      id: string;
      payload: UpdateTransactionSettingPayload;
    }) => {
      const res = await makeRequest.put(
        `/transaction-setting/tiers/${id}`,
        payload,
      );

      return res.data;
    },

    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ["transaction-settings"],
      });

      await queryClient.invalidateQueries({
        queryKey: ["transaction-setting"],
      });

      toast.success("Transaction policy updated successfully.");
    },

    onError: (error: any) => {
      toast.error(
        error?.response?.data?.message ||
          "Failed to update transaction policy.",
      );
    },
  });
};

export const useToggleTransactionSetting = () => {
  return useMutation({
    mutationFn: async ({ id, enabled }: { id: string; enabled: boolean }) => {
      const res = await makeRequest.put(
        `/transaction-setting/tiers/${id}/toggle`,
        { enabled },
      );

      return res.data;
    },

    onSuccess: async (_, variables) => {
      await queryClient.invalidateQueries({
        queryKey: ["transaction-settings"],
      });

      await queryClient.invalidateQueries({
        queryKey: ["transaction-setting", variables.id],
      });

      toast.success(
        variables.enabled
          ? "Transaction policy enabled."
          : "Transaction policy disabled.",
      );
    },

    onError: (error: any) => {
      toast.error(
        error?.response?.data?.message ||
          "Failed to update transaction policy status.",
      );
    },
  });
};

export const useDeleteTransactionSetting = () => {
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await makeRequest.delete(`/transaction-setting/tiers/${id}`);

      return res.data;
    },

    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ["transaction-settings"],
      });

      toast.success("Transaction policy deleted successfully.");
    },

    onError: (error: any) => {
      toast.error(
        error?.response?.data?.message ||
          "Failed to delete transaction policy.",
      );
    },
  });
};
